import { logger } from '../../../utils/logger';
/**
import { logger } from '../../../utils/logger';
 * Quest Creation Wizard
 * Multi-step flow: Select Quest → Invite Party → Select Classes → Start
 */

import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonBadge,
  IonList,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonIcon,
  IonText,
  useIonAlert,
  useIonLoading,
} from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { checkmarkCircle, people, documentTextOutline } from 'ionicons/icons';
import {
  getQuestTemplate,
  getStarterClasses,
  createGuildQuest,
} from '../../../services/pipsGuildQuestService';
import { QuestTemplate, PlayerClass } from '../../../types/pipsGuildQuest';
import { supabase } from '../../../services/supabase';
import { getConnections } from '../../../services/connectionsService';
import ClassSelector from './ClassSelector';
import './QuestCreation.css';

interface QuestCreationParams {
  templateId: string;
}

type CreationStep = 'select_quest' | 'invite_party' | 'select_class' | 'waiting';

const QuestCreation: React.FC = () => {
  const { templateId } = useParams<QuestCreationParams>();
  const history = useHistory();
  const [presentAlert] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();

  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<CreationStep>('select_quest');
  const [template, setTemplate] = useState<QuestTemplate | null>(null);
  const [classes, setClasses] = useState<PlayerClass[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string>('');

  // Party selection
  const [selectedParty, setSelectedParty] = useState<string[]>([]);
  const [groupMembers, setGroupMembers] = useState<any[]>([]);
  const [groupId, setGroupId] = useState<string | null>(null);

  // Class selection
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [partyClasses, setPartyClasses] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
  }, [templateId]);

  const loadData = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      // Load quest template
      const templateData = await getQuestTemplate(templateId);
      if (!templateData) {
        presentAlert({
          header: 'Error',
          message: 'Quest not found',
          buttons: ['OK'],
        });
        history.goBack();
        return;
      }
      setTemplate(templateData);

      // Load available classes
      const classesData = await getStarterClasses();
      setClasses(classesData);

      // Load user's connections (accepted matches)
      const connectionsResult = await getConnections();

      let members: any[] = [
        {
          id: user.id,
          username: 'You',
          displayName: 'You'
        }
      ];

      // Add all connected users as potential party members
      if (connectionsResult.success && connectionsResult.connections) {
        const connectionMembers = connectionsResult.connections.map((conn: any) => {
          const otherUser = conn.otherUser || conn.other_user;
          return {
            id: otherUser.id,
            username: otherUser.username || otherUser.display_name || 'Player',
            displayName: otherUser.display_name || otherUser.username || 'Player'
          };
        });
        members = [...members, ...connectionMembers];
      }

      logger.log('📋 Available party members:', members);

      // Create a temporary group for this quest
      const { data: newGroup, error: groupError } = await supabase
        .from('groups')
        .insert({
          name: `Quest Group - ${Date.now()}`,
          creator_id: user.id,
          group_type: 'private',
          images_enabled: false,
          is_quest_group: true  // Mark as quest group to exclude from general Groups tab
        })
        .select()
        .single();

      if (groupError) {
        logger.error('❌ Error creating group:', groupError);
        presentAlert({
          header: 'Error',
          message: 'Failed to create quest group. Please try again.',
          buttons: ['OK'],
        });
        return;
      }

      if (newGroup) {
        logger.log('✅ Created group:', newGroup.id);
        setGroupId(newGroup.id);

        // Add current user to the group
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({
            group_id: newGroup.id,
            user_id: user.id
          });

        if (memberError) {
          logger.error('❌ Error adding user to group:', memberError);
        }
      } else {
        logger.error('❌ No group created');
        presentAlert({
          header: 'Error',
          message: 'Failed to create quest group. Please try again.',
          buttons: ['OK'],
        });
        return;
      }

      setGroupMembers(members);

      // Auto-select current user for party
      setSelectedParty([user.id]);

      setStep('invite_party');
    } catch (error) {
      logger.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePartyMember = (userId: string) => {
    if (userId === currentUserId) return; // Can't deselect yourself

    setSelectedParty((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleContinueToClassSelection = () => {
    if (!template) return;

    if (selectedParty.length < template.minPlayers) {
      presentAlert({
        header: 'Not Enough Players',
        message: `This quest requires at least ${template.minPlayers} players.`,
        buttons: ['OK'],
      });
      return;
    }

    if (selectedParty.length > template.maxPlayers) {
      presentAlert({
        header: 'Too Many Players',
        message: `This quest allows a maximum of ${template.maxPlayers} players.`,
        buttons: ['OK'],
      });
      return;
    }

    setStep('select_class');
  };

  const handleSelectClass = (classId: string) => {
    setSelectedClass(classId);
  };

  const handleConfirmClass = () => {
    if (!selectedClass) {
      presentAlert({
        header: 'No Class Selected',
        message: 'Please select a class to continue.',
        buttons: ['OK'],
      });
      return;
    }

    // Save this user's class selection
    setPartyClasses((prev) => ({
      ...prev,
      [currentUserId]: selectedClass,
    }));

    // For solo play or if all players ready, start quest
    // In multiplayer, would wait for other players
    handleStartQuest();
  };

  const handleStartQuest = async () => {
    if (!template) return;

    logger.log('🎮 Starting quest with groupId:', groupId);

    // For now, if solo play, auto-select classes for other party members
    const finalPartyClasses = { ...partyClasses };
    for (const userId of selectedParty) {
      if (!finalPartyClasses[userId]) {
        // Auto-assign a random class for demo purposes
        finalPartyClasses[userId] = classes[Math.floor(Math.random() * classes.length)].id;
      }
    }

    if (!groupId) {
      logger.error('❌ No groupId available when trying to start quest');
      presentAlert({
        header: 'Error',
        message: 'No group found. Please try again.',
        buttons: ['OK'],
      });
      return;
    }

    await presentLoading({ message: 'Creating quest...' });

    try {
      // Add selected party members to the group
      const membersToAdd = selectedParty.filter(id => id !== currentUserId);
      if (membersToAdd.length > 0) {
        const groupMemberInserts = membersToAdd.map(userId => ({
          group_id: groupId,
          user_id: userId
        }));

        await supabase
          .from('group_members')
          .insert(groupMemberInserts);
      }

      const result = await createGuildQuest(
        groupId,
        template.id,
        selectedParty,
        finalPartyClasses
      );

      await dismissLoading();

      if (result.success && result.questId) {
        presentAlert({
          header: 'Quest Created!',
          message: 'Your adventure begins...',
          buttons: [
            {
              text: 'Begin',
              handler: () => {
                history.push(`/games/guild-quest/play/${result.questId}`);
              },
            },
          ],
        });
      } else {
        presentAlert({
          header: 'Error',
          message: result.error || 'Failed to create quest',
          buttons: ['OK'],
        });
      }
    } catch (error: any) {
      await dismissLoading();
      presentAlert({
        header: 'Error',
        message: error.message || 'Failed to create quest',
        buttons: ['OK'],
      });
    }
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/games/pips-guild-quest" />
            </IonButtons>
            <IonTitle>Create Quest</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="loading-container">
            <IonSpinner />
            <p>Loading...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/pips-guild-quest" />
          </IonButtons>
          <IonTitle>Create Quest</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="quest-creation">
        {/* Progress Steps */}
        <div className="creation-steps">
          <div className={`step ${step === 'invite_party' ? 'active' : ''} ${partyClasses[currentUserId] ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Invite Party</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${step === 'select_class' ? 'active' : ''} ${partyClasses[currentUserId] ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Select Class</div>
          </div>
          <div className="step-divider"></div>
          <div className={`step ${step === 'waiting' ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Begin</div>
          </div>
        </div>

        {/* Quest Info */}
        {template && (
          <IonCard className="quest-info-card">
            <IonCardHeader>
              <IonCardTitle>
                <IonIcon icon={documentTextOutline} /> {template.name}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <p>{template.description}</p>
              <div className="quest-meta">
                <IonBadge color="primary">
                  {template.minPlayers}-{template.maxPlayers} Players
                </IonBadge>
                <IonBadge color="secondary">{template.difficulty}</IonBadge>
                <IonBadge color="tertiary">~{template.estimatedNodes} nodes</IonBadge>
              </div>
            </IonCardContent>
          </IonCard>
        )}

        {/* Step Content */}
        <div className="step-content">
          {step === 'invite_party' && (
            <div className="party-selection">
              <h2>
                <IonIcon icon={people} /> Select Party Members
              </h2>
              <IonText color="medium">
                <p>Choose who will join you on this quest.</p>
              </IonText>

              <IonCard>
                <IonList>
                  {groupMembers.map((member) => (
                    <IonItem key={member.id}>
                      <IonCheckbox
                        slot="start"
                        checked={selectedParty.includes(member.id)}
                        disabled={member.id === currentUserId}
                        onIonChange={() => handleTogglePartyMember(member.id)}
                      />
                      <IonLabel>
                        <h3>{member.displayName || member.username}</h3>
                        {member.id === currentUserId && (
                          <p>
                            <IonBadge color="primary">You</IonBadge>
                          </p>
                        )}
                      </IonLabel>
                    </IonItem>
                  ))}
                </IonList>
              </IonCard>

              <div className="party-count">
                <IonText>
                  Selected: <strong>{selectedParty.length}</strong> /{' '}
                  {template?.minPlayers}-{template?.maxPlayers} players
                </IonText>
              </div>

              <IonButton
                expand="block"
                size="large"
                onClick={handleContinueToClassSelection}
                className="continue-button"
              >
                Continue
              </IonButton>
            </div>
          )}

          {step === 'select_class' && (
            <div className="class-selection">
              <ClassSelector
                classes={classes}
                selectedClassId={selectedClass}
                onSelectClass={handleSelectClass}
              />

              <IonButton
                expand="block"
                size="large"
                onClick={handleConfirmClass}
                disabled={!selectedClass}
                className="continue-button"
              >
                {selectedParty.length === 1 ? 'Start Quest' : 'Confirm Class'}
              </IonButton>
            </div>
          )}

          {step === 'waiting' && (
            <div className="waiting-screen">
              <div className="waiting-icon">⏳</div>
              <h2>Waiting for Party...</h2>
              <IonText color="medium">
                <p>Waiting for other players to select their classes.</p>
              </IonText>

              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Party Status</IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonList>
                    {selectedParty.map((userId) => {
                      const member = groupMembers.find((m) => m.id === userId);
                      const hasClass = !!partyClasses[userId];

                      return (
                        <IonItem key={userId} lines="none">
                          <IonLabel>
                            <h3>{member?.displayName || 'Player'}</h3>
                          </IonLabel>
                          {hasClass ? (
                            <IonIcon icon={checkmarkCircle} color="success" slot="end" />
                          ) : (
                            <IonSpinner slot="end" />
                          )}
                        </IonItem>
                      );
                    })}
                  </IonList>
                </IonCardContent>
              </IonCard>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default QuestCreation;
